import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Atom, MagnifyingGlass, FloppyDisk, GitMerge, Warning } from '@phosphor-icons/react';

/**
 * Skills Vocabulary — cross-family normalized skill catalog.
 * Auto-derived from role_families.required_skills, specialization_tags, and
 * assessment items[].skills. Useful for spotting taxonomy duplication and
 * seeing which skills span sectors.
 */
const SkillsVocabularyAdmin = () => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [persisting, setPersisting] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [merging, setMerging] = useState(null); // group's normalized id

  const load = async () => {
    const [vocab, dups] = await Promise.all([
      api.get('/skills-vocabulary/derive'),
      api.get('/skills-vocabulary/duplicates'),
    ]);
    setData(vocab.data);
    setDuplicates(dups.data);
  };
  useEffect(() => { load(); }, []);

  const persist = async () => {
    setPersisting(true);
    try {
      const { data } = await api.post('/skills-vocabulary/persist');
      toast.success(`Persisted ${data.persisted} skills (${data.cross_family_skills} cross-family).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Persist failed.');
    } finally {
      setPersisting(false);
    }
  };

  const merge = async (group, canonicalSlug) => {
    const dups = group.members.map((m) => m.slug).filter((s) => s !== canonicalSlug);
    setMerging(group.normalized);
    try {
      const { data } = await api.post('/skills-vocabulary/merge', {
        canonical_slug: canonicalSlug,
        duplicate_slugs: dups,
      });
      toast.success(`Merged → "${canonicalSlug}". Updated ${data.role_families_required_updated + data.role_families_spec_updated} families and ${data.assessments_spec_updated + data.assessments_items_updated} tests.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed.');
    } finally {
      setMerging(null);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let list = data.skills;
    if (q) list = list.filter((s) => s.slug.includes(q) || s.label.toLowerCase().includes(q));
    return list.sort((a, b) => b.family_count - a.family_count || a.slug.localeCompare(b.slug));
  }, [data, search]);

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-primary mb-2">ADMIN · SKILLS VOCABULARY</div>
            <h1 className="font-display font-black text-4xl tracking-tighter">One vocabulary across families.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Cross-family skill catalog auto-derived from family `required_skills`, `specialization_tags`, and assessment items. Helps you spot tag duplication ("communication" vs "communications") and see which skills span sectors.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/role-families"><Button variant="outline" className="rounded-none" data-testid="goto-families-btn">← Role families</Button></Link>
            <Button onClick={persist} disabled={persisting} className="rounded-none" data-testid="persist-vocab-btn">
              <FloppyDisk size={16} className="mr-2" /> {persisting ? 'Persisting…' : 'Snapshot'}
            </Button>
          </div>
        </div>

        {data && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="border border-border bg-white p-4">
              <div className="label-eyebrow text-muted-foreground">TOTAL SKILLS</div>
              <div className="font-display font-black text-3xl mt-1" data-testid="total-skills">{data.total_skills}</div>
            </div>
            <div className="border border-border bg-white p-4">
              <div className="label-eyebrow text-muted-foreground">CROSS-FAMILY (2+ families)</div>
              <div className="font-display font-black text-3xl mt-1" data-testid="cross-family-skills">{data.cross_family_skills}</div>
            </div>
            <div className="border border-border bg-white p-4">
              <div className="label-eyebrow text-muted-foreground">SINGLE-FAMILY</div>
              <div className="font-display font-black text-3xl mt-1">{data.total_skills - data.cross_family_skills}</div>
            </div>
          </div>
        )}

        {duplicates && duplicates.group_count > 0 && (
          <div className="border border-amber-300 bg-amber-50 p-5 mb-6" data-testid="duplicates-panel">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={18} weight="fill" className="text-amber-600" />
              <div className="font-display font-black text-lg">{duplicates.group_count} possible duplicate group{duplicates.group_count !== 1 ? 's' : ''}</div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              These skills normalize to the same canonical form (case · plural · hyphen vs underscore). Pick the winner to merge — every family + assessment + item.skills will be updated atomically.
            </p>
            <div className="space-y-3">
              {duplicates.groups.map((g) => (
                <div key={g.normalized} className="border border-amber-200 bg-white p-3" data-testid={`dup-group-${g.normalized}`}>
                  <div className="text-xs text-muted-foreground mb-2 mono">normalizes → {g.normalized}</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {g.members.map((m, idx) => (
                      <React.Fragment key={m.slug}>
                        {idx > 0 && <span className="text-muted-foreground text-sm">vs</span>}
                        <Button
                          size="sm"
                          className="rounded-none h-8"
                          variant="outline"
                          onClick={() => merge(g, m.slug)}
                          disabled={merging === g.normalized}
                          data-testid={`merge-into-${m.slug}`}
                          title={`Merge all into "${m.slug}"`}
                        >
                          <GitMerge size={12} className="mr-1" />
                          <span className="mono text-xs">{m.slug}</span>
                          <span className="ml-1.5 text-muted-foreground">({m.family_count}f · {m.assessment_count}t)</span>
                        </Button>
                      </React.Fragment>
                    ))}
                    {merging === g.normalized && <span className="text-xs text-muted-foreground">Merging…</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="relative mb-4">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search skills…"
            className="pl-10 rounded-none h-11"
            data-testid="vocab-search"
          />
        </div>

        <div className="border border-border" data-testid="vocab-list">
          {filtered.slice(0, 200).map((s) => (
            <div key={s.slug} className="p-4 bg-white border-b border-border flex items-start justify-between gap-4" data-testid={`vocab-${s.slug}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Atom size={14} className="text-primary" />
                  <div className="font-medium">{s.label}</div>
                  <code className="text-xs text-muted-foreground mono">{s.slug}</code>
                </div>
                {s.family_slugs.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {s.family_slugs.slice(0, 6).map((f) => (
                      <span key={f} className="label-eyebrow bg-secondary px-2 py-0.5 mono">{f}</span>
                    ))}
                    {s.family_slugs.length > 6 && (
                      <span className="label-eyebrow text-muted-foreground">+{s.family_slugs.length - 6} more</span>
                    )}
                  </div>
                )}
                {s.sectors.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {s.sectors.join(' · ')}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="mono font-bold text-lg">{s.family_count}</div>
                <div className="mono text-[10px] text-muted-foreground">families</div>
                <div className="mono text-[10px] text-muted-foreground mt-1">{s.assessment_count} tests</div>
              </div>
            </div>
          ))}
          {filtered.length > 200 && (
            <div className="p-3 text-xs text-center text-muted-foreground">+ {filtered.length - 200} more — refine your search.</div>
          )}
        </div>
      </div>
    </Shell>
  );
};

export default SkillsVocabularyAdmin;
